import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useRef, useState } from 'react';
import usePrivacyContext from 'src/hooks/privacy/usePrivacyContext';
import { FAQItem } from 'src/contexts/privacy/PrivacyContext';
import useWindowDimensions from 'src/hooks/useWindowDimensionHook';
import Page from 'src/components/Page';
import BoxTitlePage from 'src/theme/overrides/BoxTitlePage';
import { HEADER } from 'src/config';
import { getScrollbarStyles } from 'src/theme/scrollbar';
import { ListBoxProps } from 'src/utils/components/autocomplete';
import { useNavigate } from 'react-router';
import { PATH_PRIVACY } from 'src/routes/paths';
// ----------------------------------------------------------------------

const CATEGORY_LABELS: Record<string, string> = {
  servizi: 'Services',
  privacy: 'Privacy & Security',
};

const CATEGORY_COLORS: Record<string, 'default' | 'primary' | 'info'> = {
  servizi: 'info',
  privacy: 'primary',
};
const CHIP_COL_WIDTH = 140;

const matchesSearch = (item: FAQItem, query: string) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const categoryLabel = (CATEGORY_LABELS[item.category] ?? '').toLowerCase();

  const isCategoryQuery = Object.values(CATEGORY_LABELS).some((label) => label.toLowerCase() === q);
  if (isCategoryQuery) return categoryLabel === q;

  return `${item.question} ${item.answer} ${categoryLabel}`.toLowerCase().includes(q);
};

export default function PageQA() {
  const theme = useTheme();
  const { height } = useWindowDimensions();
  const { faqItems } = usePrivacyContext();
  const [expanded, setExpanded] = useState<string | false>(false);
  const [search, setSearch] = useState('');
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const navigate = useNavigate();

  const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const filtered: FAQItem[] = faqItems.filter((item) => matchesSearch(item, search));

  const handleSelect = (_: React.SyntheticEvent, value: FAQItem | string | null) => {
    if (!value || typeof value === 'string') return;
    setSearch('');
    setExpanded(value.id);
    setTimeout(() => {
      itemRefs.current[value.id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const contentHeight =
    height - HEADER.DASHBOARD_DESKTOP_BANNER_HEIGHT - HEADER.DASHBOARD_DESKTOP_UNDER_BANNER_HEIGHT - 70;

  return (
    <Page title="Q&A">
      <BoxTitlePage>
        <Typography variant="h2">Question & Answer</Typography>
      </BoxTitlePage>
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          width: '100%',
          height: contentHeight,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          ...getScrollbarStyles(theme),
        }}
      >
        {/* Search */}
        <Box mb={3}>
          <Autocomplete<FAQItem, false, false, true>
            freeSolo
            disablePortal
            options={faqItems}
            value={null}
            inputValue={search}
            onInputChange={(_, value) => setSearch(value)}
            onChange={handleSelect}
            // nessun suggerimento finché l'input è vuoto
            filterOptions={(options, state) =>
              state.inputValue.trim() ? options.filter((o) => matchesSearch(o, state.inputValue)) : []
            }
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.question)}
            ListboxProps={ListBoxProps}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                {option.question}
              </Box>
            )}
            renderInput={(params) => <TextField {...params} variant="filled" label="Search FAQ" />}
            sx={{ minWidth: 320, maxWidth: 480 }}
          />
        </Box>

        {/* FAQ list */}
        {filtered.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">No FAQ matches your search.</Typography>
          </Box>
        ) : (
          <Stack spacing={1} mb={2}>
            {filtered.map((item) => (
              <Accordion
                key={item.id}
                ref={(el) => {
                  itemRefs.current[item.id] = el;
                }}
                expanded={expanded === item.id}
                onChange={handleAccordionChange(item.id)}
                disableGutters
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: expanded === item.id ? 'primary.main' : 'divider',
                  borderRadius: '10px !important',
                  '&:before': { display: 'none' },
                  transition: 'border-color 0.2s',
                  scrollMarginTop: 8,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 2.5,
                    py: 0.5,
                    borderRadius: 2,
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      gap: 2,
                      // prevent content from overlapping expand icon
                      mr: 1,
                    },
                  }}
                >
                  {/* Question — takes all available space */}
                  <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                    {item.question}
                  </Typography>

                  {/* Category chip — fixed width column, right-aligned */}
                  <Box sx={{ width: CHIP_COL_WIDTH, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                    <Chip
                      label={CATEGORY_LABELS[item.category]}
                      size="small"
                      color={CATEGORY_COLORS[item.category] ?? 'default'}
                      variant="outlined"
                      sx={{ fontSize: '0.65rem', height: 20, borderRadius: 4 }}
                    />
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.7, whiteSpace: 'pre-line' }}
                  >
                    {item.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}

        {/* Footer note */}
        <Box mt="auto" px={2} py={0.5} pb={1} sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Can't find an answer?{' '}
            <Typography
              component="span"
              variant="caption"
              color="primary.main"
              sx={{ cursor: 'pointer', fontWeight: 600 }}
              onClick={() => navigate(PATH_PRIVACY.contactUs)}            >
              Contact us
            </Typography>
          </Typography>
        </Box>
      </Box>
    </Page>
  );
}